import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  DrawLineStringTool,
  DrawPointTool,
  DrawPolygonTool,
  Feature,
} from "@inovitas/infra3dsdk/dist/types/mm";
import { IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { UseActiveLayer } from "shared/common/hooks/UseActiveLayer";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import {
  editTools,
  EditToolsType,
} from "shared/common/utils/editTools/editTools";
import {
  EditIcon,
  EditIconActive,
} from "shared/common/utils/icons/measurementIcons";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";
import PopoverBox from "shared/common/utils/popover/PopoverBox";
import LayerContent from "./LayerContent";

export default function Edit({ authMode }: { authMode: "view" | "edit" }) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const layerConfiguration = defaultStore((state) => state.layerConfiguration);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const changeAlertSaveOrDiscardLastLayer = defaultStore(
    (state) => state.changeAlertSaveOrDiscardLastLayer
  );

  const editPopover = sidebarStore((state) => state.editPopover);
  const changeEditPopover = sidebarStore((state) => state.changeEditPopover);
  const editToolActivated = sidebarStore((state) => state.editToolActivated);
  const changeEditToolActivated = sidebarStore(
    (state) => state.changeEditToolActivated
  );
  const closeAllToolPopover = sidebarStore(
    (state) => state.closeAllToolPopover
  );
  const activeEditTool = sidebarStore((state) => state.activeEditTool);
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );
  const changeShowToolDescription = sidebarStore(
    (state) => state.changeShowToolDescription
  );
  const enableEditFeature = sidebarStore((state) => state.enableEditFeature);
  const enableDeleteFeature = sidebarStore(
    (state) => state.enableDeleteFeature
  );
  const activeEditModeLayers = sidebarStore(
    (state) => state.activeEditModeLayers
  );
  const changeActiveEditModeLayers = sidebarStore(
    (state) => state.changeActiveEditModeLayers
  );
  const changeEditMode = sidebarStore((state) => state.changeEditMode);

  const activeLayer = UseActiveLayer(viewer);

  const [editModes, setEditModes] = useState<EditToolsType[]>([]);
  const [point, setPoint] = useState<DrawPointTool>();
  const [polygon, setPolygon] = useState<DrawPolygonTool>();
  const [linestring, setLinestring] = useState<DrawLineStringTool>();
  const [editModeAddFeature, setEditModeAddFeature] = useState<
    DrawLineStringTool | DrawPolygonTool | DrawPointTool | undefined
  >(undefined);
  const [editModeMoveFeature, setEditModeMoveFeature] = useState<
    mm.EditFeatureTool | undefined
  >(undefined);
  const [editModeSelectFeature, setEditModeSelectFeature] = useState<
    mm.SelectFeatureTool | undefined
  >(undefined);

  //----------------------- add feature mode -------------------------
  const updateInfoBar = () => {};
  const reset = () => {};
  const enable = () => {};
  const disable = () => {};
  const addfeature = (info: any) => {
    if (!viewer) return;
    const activeLayer = viewer.getScene().getLayerHandler().getActiveLayer();
    if (activeLayer === null) return;
    const geometry = activeLayer.geometrytype;
    let feature = null;
    switch (geometry) {
      case mm.GEOMETRYTYPE.POINT:
        feature = info.pointfeature;
        break;
      case mm.GEOMETRYTYPE.LINESTRING:
        feature = info.linestringfeature;
        break;
      case mm.GEOMETRYTYPE.POLYGON:
        feature = info.polygonfeature;
        break;
    }

    feature.properties = activeLayer.getDefaultProperties();

    activeLayer
      .getSystempropertyHandler()
      .handleSystemProperties(
        feature,
        "onedit",
        info.mx,
        info.my,
        info.measurepoint
      );
    const insertedList = activeLayer.getLayerEditor().featuresinsert.features;
    if (insertedList.length === 0) {
      changeShowToolDescription(false);
    }

    const featureinserts: mm.Feature = activeLayer
      .getLayerEditor()
      .insertFeatures(feature) as mm.Feature;
    if (feature.id === null) {
      feature.geometry["id"] = featureinserts.geometry.id;
    }

    changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
  };

  const drawOptions: any = {
    layerstyle: viewer?.getScene().getToolHandler().defaultstyles.draw,
    highlightlayerstyle: new mm.FeatureStyle({
      stylename: "drawcontroller_highlightlayerstyle",
      rules: [
        {
          filter: "",
          symbolizer: "point",
          params: {
            "font-marker": {
              name: "circle-o",
            },
            size: 10,
            stroke: [1, 1, 0, 1.0],
          },
        },
        {
          filter: "",
          symbolizer: "line",
          params: {
            "stroke-width": 3,
            stroke: [1, 1, 0, 1.0],
          },
        },
        {
          filter: "",
          symbolizer: "polygon",
          params: {
            fill: [1, 1, 0, 0.4],
          },
        },
      ],
    }),
    finishedcbf: addfeature,
    disablecbf: disable,
    enablecbf: enable,
    resetcbf: reset,
    maxdist: 50,
    pointsetcbf: updateInfoBar,
    continousdrawcbf: updateInfoBar,
  };
  //----------------------- end add feature mode -------------------------
  //----------------------- move feature mode ----------------------------
  const moveFeature = (feature: mm.Feature, ptidx: any, evt: any) => {
    if (viewer === undefined) return;
    const mx = evt.offsetX;
    const my = evt.offsetY;

    const feature_json = feature.toGeoJSON();

    const activeLayer = viewer.getScene().getLayerHandler().getActiveLayer();
    if (activeLayer === null) return;
    if (feature_json.id !== null) {
      // If the feature is already updated, use the already updated feature.
      // Otherwise the previous update will be lost.
      const updating_feature =
        activeLayer.getLayerEditor().featuresupdate[feature_json.id] ||
        feature_json;
      activeLayer
        .getSystempropertyHandler()
        .handleSystemProperties(updating_feature, "onedit", mx, my);
      updating_feature.geometry = feature_json.geometry;
      activeLayer.getLayerEditor().updateFeatures(updating_feature);
    }
    changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
  };

  const moveOptions: any = {
    editedcbf: moveFeature,
    disablecbf: disable,
    enablecbf: () => {
      viewer?.getCanvas().focus();
    },
    resetcbf: reset,
    activecursor: "pointer",
    edit: true,
    maxdist: 50,
  };

  useEffect(() => {
    if (activeLayer && editModeMoveFeature) {
      if (activeLayer.getLayerEditor() !== null) {
        editModeMoveFeature.options.picklayer = [
          activeLayer.getFeatureCollection(),
          activeLayer.getLayerEditor().featuresinsert,
        ];
      } else {
        editModeMoveFeature.options.picklayer = [
          activeLayer.getFeatureCollection(),
        ];
      }
    } else if (activeLayer && editModeSelectFeature) {
      if (activeLayer.getLayerEditor() !== null) {
        editModeSelectFeature.options.picklayer = [
          activeLayer.getFeatureCollection(),
          activeLayer.getLayerEditor().featuresinsert,
        ];
      } else {
        editModeSelectFeature.options.picklayer = [
          activeLayer.getFeatureCollection(),
        ];
      }
    }
  }, [activeLayer]);
  //----------------------- end move feature mode ------------------------
  //----------------------- select feature mode ------------------------
  const picklayers: mm.Layer[] = [];
  const onSelect = (feature: Feature) => {
    if (viewer === undefined) return;
    const activeLayer = viewer.getScene().getLayerHandler().getActiveLayer();
    const parentFeatureLayer = viewer
      .getScene()
      .getLayerHandler()
      .getLayerByFeatureCollection(feature.parentcollection);
    if (activeLayer && activeLayer.id === parentFeatureLayer.id) {
      activeLayer.getLayerEditor().deleteFeatures([feature.id]);
      if (activeLayer.getLayerEditor().featuresdelete.length > 0) {
        const deletedFeatures = activeLayer.getLayerEditor().featuresdelete;
        //check if its deleted this feature and add it inside list by converting to feature from id
        if (deletedFeatures.includes(feature.id)) {
          if (activeLayer.getFeatureById(feature.id)) {
            changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
            activeLayer.getFeatureById(feature.id).hide();
          } else {
            changeAlertMessage({
              message: t("alert_error.can_not_delete_feature_id_error"),
              type: "error",
            });
          }
        }
      }
    } else {
      changeAlertMessage({
        message: t("alert_error.can_not_delete_feature"),
        type: "error",
      });
    }
  };

  const tooloptions = {
    selectedcbf: onSelect,
    deselectedcbf: function (feature: mm.Feature) {
      if (viewer === undefined) return;
      const layer = viewer
        .getScene()
        .getLayerHandler()
        .getLayerByFeatureCollection(feature.parentcollection);
      layer.featureselector.removeFromSelection(feature.id);
    },
    clearcbf: function () {
      for (let i = 0; i < picklayers.length; i++) {
        picklayers[i].featureselector.clearSelection();
      }
    },
    hightlightonhover: true,

    activecursor: "pointer",
    clearonnullpick: true,
    selectionmodel: mm.SELECTIONMODEL.SINGLE,
  };
  //----------------------- end select feature mode ------------------------

  //change table button and popover
  const handleClickEdit = () => {
    if (editToolActivated === "") {
      changeEditToolActivated("add");
    }
    closeAllToolPopover();
    changeEditPopover(!editPopover);
  };

  //edit tools list
  const editToolsList: EditToolsType[] = editTools();

  // add edit tools buttons only the first time rendering this component
  useEffect(() => {
    setEditModes(editToolsList);
  }, []);

  //create options for drawing any type of features
  useEffect(() => {
    if (viewer === undefined) return;
    setPoint(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawPointTool(drawOptions, "drawPoint", "edit")
    );
    setPolygon(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawPolygonTool(drawOptions, "drawPolygon", "edit")
    );
    setLinestring(
      viewer
        .getScene()
        .getToolHandler()
        .createDrawLineStringTool(drawOptions, "drawLineString", "edit")
    );
    setEditModeMoveFeature(
      viewer
        .getScene()
        .getToolHandler()
        .createEditFeatureTool(moveOptions, "moveFeature")
    );
    setEditModeSelectFeature(
      viewer
        .getScene()
        .getToolHandler()
        .createSelectFeatureTool(tooloptions, "selectFeature")
    );
  }, [viewer]);

  useEffect(() => {
    if (activeLayer) {
      let event_nr: number | undefined = undefined;
      if (editModeSelectFeature !== undefined && viewer !== undefined) {
        editModeSelectFeature.addToPickLayer(
          activeLayer.getFeatureCollection()
        );

        event_nr = viewer.getScene().userInputHandler.subscribeEvent(
          mm.USERINPUTTYPE["LAYERADDED"],
          function (layer: mm.Layer) {
            editModeSelectFeature.addToPickLayer(layer.getFeatureCollection());
            if (layer.getLayerEditor()) {
              editModeSelectFeature.addToPickLayer(
                layer.getLayerEditor().featuresinsert
              );
            }
          },
          undefined
        );
      }
      return () => {
        if (!event_nr || viewer === undefined) return;
        viewer
          .getScene()
          .userInputHandler.unSubscribeEvent(
            mm.USERINPUTTYPE["LAYERADDED"],
            event_nr
          );
      };
    }
  }, [editModeSelectFeature, activeLayer]);

  // change edit mode while changing active layer
  useEffect(() => {
    if (
      activeLayer !== undefined &&
      activeLayer !== null &&
      viewer !== undefined
    ) {
      changeFeatureTableAddedRows(undefined);
      if (mm.GEOMETRYTYPE.POINT == activeLayer.geometrytype) {
        setEditModeAddFeature(point);
      } else if (mm.GEOMETRYTYPE.LINESTRING == activeLayer.geometrytype) {
        setEditModeAddFeature(linestring);
      } else if (mm.GEOMETRYTYPE.POLYGON == activeLayer.geometrytype) {
        setEditModeAddFeature(polygon);
      }
    } else {
      if (editModeAddFeature) {
        editModeAddFeature.disable();
      }
      setEditModeAddFeature(undefined);
    }
  }, [activeLayer]);

  //controll if last active layer have changes while was on edit mode than show message for discarded changes
  const checkForLastLayerDiscardedChanges = () => {
    if (!viewer) return;
    const haveChanges = activeEditModeLayers
      ? getChangedFeaturesList(activeEditModeLayers)
      : [];
    if (haveChanges.length > 0) {
      changeAlertSaveOrDiscardLastLayer(true);
    } else {
      changeActiveEditModeLayers(activeLayer);
    }
  };

  //deactivate edit mode when you select anothertype of layer
  const handleActiveEditModeLayersList = () => {
    if (!viewer) return;
    if (activeEditModeLayers) {
      if (activeEditModeLayers.id === activeLayer?.id) {
        changeActiveEditModeLayers(activeLayer);
        return;
      } else {
        if (activeEditModeLayers.getLayerEditor()) {
          checkForLastLayerDiscardedChanges();
        }
        if (activeLayer?.mode === 1) {
          if (
            (activeLayer.geometrytype == mm.GEOMETRYTYPE.LINESTRING ||
              activeLayer.geometrytype == mm.GEOMETRYTYPE.POLYGON ||
              activeLayer.geometrytype == mm.GEOMETRYTYPE.POINT) &&
            activeLayer.editable
          ) {
            changeEditMode(true);
          }
          changeFeatureTableAddedRows(getChangedFeaturesList(activeLayer));
        } else {
          activeEditModeLayers.setEditMode(false);
          changeEditMode(false);
        }
      }
    } else {
      changeActiveEditModeLayers(activeLayer);
    }
  };

  useEffect(() => {
    if (editPopover && activeLayer && activeLayer.editable) {
      activeLayer.setEditMode(true);
      handleActiveEditModeLayersList();
    }

    // Disable all tools first, then enable the active one to avoid cursor resets
    if (
      editModeAddFeature &&
      !(
        editPopover &&
        activeLayer &&
        editToolActivated === "add" &&
        activeLayer.editable
      )
    ) {
      editModeAddFeature.disable();
    }
    if (
      editModeMoveFeature &&
      !(
        editPopover &&
        activeLayer &&
        editToolActivated === "edit" &&
        activeLayer.editable
      )
    ) {
      editModeMoveFeature.disable();
    }
    if (
      editModeSelectFeature &&
      !(
        editPopover &&
        activeLayer &&
        editToolActivated === "delete" &&
        activeLayer.editable
      )
    ) {
      editModeSelectFeature.disable();
    }

    // Now enable the active tool
    if (editPopover && activeLayer && activeLayer.editable) {
      if (editToolActivated === "add" && editModeAddFeature) {
        editModeAddFeature.enable();
      } else if (editToolActivated === "edit" && editModeMoveFeature) {
        editModeMoveFeature.enable();
      } else if (editToolActivated === "delete" && editModeSelectFeature) {
        editModeSelectFeature.enable();
      }
    }
  }, [
    activeLayer,
    editToolActivated,
    editModeAddFeature,
    editModeMoveFeature,
    editModeSelectFeature,
    editPopover,
  ]);

  //activate or deactiate move feature mode on Layer tree edit mode
  useEffect(() => {
    if (!activeLayer) return;
    if (editModeMoveFeature && !editPopover) {
      if (enableEditFeature && activeLayer.editable) {
        activeLayer.setEditMode(true);
        handleActiveEditModeLayersList();
        editModeMoveFeature.enable();
      } else {
        editModeMoveFeature.disable();
      }
    }
    if (enableDeleteFeature && activeLayer.editable) {
      activeLayer.setEditMode(true);
      handleActiveEditModeLayersList();
    }
  }, [enableEditFeature, enableDeleteFeature]);

  return (
    <>
      <Tooltip
        title={t("tooltip.edit")}
        placement="right"
        enterDelay={400}
        classes={{
          tooltip: "infra3d-buttonDescription",
        }}
      >
        <IconButton
          className="onboarding-editTools"
          sx={{
            width: "40px",
            mx: "auto",
            color: "white",
            "@media (hover: hover)": {
              ":hover": { color: "secondary.main" },
            },
          }}
          classes={{
            root: editPopover
              ? "infra3d-iconButtonRippleActive"
              : "infra3d-iconButtonRipple",
          }}
          onClick={() =>
            monitor(apm, "change-status-edit | Toolbar", handleClickEdit)
          }
          disableRipple={true}
        >
          {activeEditTool && editPopover ? (
            editModes.find(
              (item: EditToolsType) => item.id === editToolActivated
            )?.icon
          ) : editPopover ? (
            <EditIconActive />
          ) : (
            <EditIcon />
          )}
        </IconButton>
      </Tooltip>
      <PopoverBox visible={editPopover}>
        {viewer !== undefined && layerConfiguration !== undefined && (
          <LayerContent authMode={authMode} />
        )}
      </PopoverBox>
    </>
  );
}
